import React, { useState, useEffect } from 'react';
import { CircleAlert, TabletSmartphone } from 'lucide-react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { get, isNil, isEmpty } from 'lodash';

import { checkinAction, nextAction, uploadImageToPreSignedUrlAction } from 'actions/workflow';
import { Button } from 'components/Button';
import { FormRow } from 'components/Form';
import IDScan from 'components/IDScan';
import Modal from 'components/Modal';
import { convertBase64ToImage } from 'utils/image';
import useWorkflowGuard from 'hooks/useWorkflowGuard';
import { securityWaitingMessage } from 'utils/securityWaitingMessage';
import Spinner from 'components/LoaderWithMessage';
import { useConfig } from 'config/ConfigContext';
import { isDesktopEnvironment } from 'utils/helper';
import { SEND_TEXT_COOLDOWN_SECONDS } from 'utils/constants';

let idScanSocket = null;
let timeoutTimer = null;
const NORMAL_CLOSE = 'Normal close';
let securityWaitingMessageTimer = null;

const VerifyIdentity = ({
  navigate,
  dispatch,
  searchParams,
}) => {
  const { websocketUrl, verificationInterval } = useConfig();
  useWorkflowGuard(searchParams, navigate);
  const [hasVerifiedDevice, setHasVerifiedDevice] = useState(false);
  const [IDScanData, setIDScanData] = useState(null);
  const [scanErrorMessage, setScanErrorMessage] = useState('');
  const [securityWaitingMessageStep, setSecurityWaitingMessageStep] = useState(null);
  const [isSecurityModalVisible, setIsSecurityModalVisible] = useState(false);
  const [isProgressVisible, setIsProgressVisible] = useState(false);
  const [showDesktopModal, setShowDesktopModal] = useState(false);
  const [showIdScan, setShowIdScan] = useState(false);
  const [isSendingText, setIsSendingText] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [sendTextError, setSendTextError] = useState(null);
  const [idScanUploadFallback, setIdScanUploadFallback] = useState(false);
  const checkinData = useSelector((state) => get(state, 'workflow'));
  const frontImagePreSignedUrl = get(checkinData, 'data.frontImagePreSignedUrl');
  const backOrSecondImagePreSignedUrl = get(checkinData, 'data.backOrSecondImagePreSignedUrl');
  const selfieImagePreSignedUrl = get(checkinData, 'data.selfieImagePreSignedUrl');

  const getBlackbox = () => {
    if (!window.IGLOO || typeof window.IGLOO.getBlackbox !== 'function') {
      console.log('❌ IGLOO not ready — run a collect first');
      return;
    }
    const result = window.IGLOO.getBlackbox();
    console.log('result.blackbox:', result.blackbox);
    return result.blackbox || '';
  };

  useEffect(() => {
    if (securityWaitingMessageStep !== null) {
      securityWaitingMessageTimer = setInterval(() => {
        setSecurityWaitingMessageStep((securityWaitingMessageStep + 1) % 7);
      }, 15000);
    }

    return () => {
      clearInterval(securityWaitingMessageTimer);
    };
  }, [securityWaitingMessageStep]);

  useEffect(() => {
    if (cooldownSeconds > 0) {
      const timer = setTimeout(() => setCooldownSeconds(cooldownSeconds - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldownSeconds]);

  useEffect(() => {
    if (isDesktopEnvironment()) {
      setShowDesktopModal(true);
    } else {
      setShowIdScan(true);
    }
    setHasVerifiedDevice(true);
  }, [])

  useEffect(() => {
    if (!hasVerifiedDevice && isNil(checkinData) || isEmpty(checkinData)) {
      return;
    }

    if (document && document.body && !document.getElementById('ioBlackBox')) {
      const hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.id = 'ioBlackBox';
      hidden.name = 'ioBlackBox';
      document.body.appendChild(hidden);
    }

    window.io_global_object_name = 'IGLOO';
    window.IGLOO = {
      loader: {
        uri_hook: '/iojs/',
        version: 'general5',
        subkey: '',
      },
    };
    const url = '/assets/js/iovation_loader_only.js';

    const script = document.createElement('script');
    script.async = true;
    script.src = url;

    document.head.appendChild(script);
  }, [checkinData, hasVerifiedDevice]);

  const handleContinueOnDesktop = (e) => {
    e.preventDefault();
    setShowDesktopModal(false);
    setShowIdScan(true);
  }

  const handleResendText = (e) => {
    e.preventDefault();
    setIsSendingText(true);
    setSendTextError(null);

    dispatch(checkinAction({
      key: searchParams.get('key'),
      data: { sendText: true },
      success: () => {
        // setIsTextSent(true);
        setIsSendingText(false);
        setCooldownSeconds(SEND_TEXT_COOLDOWN_SECONDS);
      },
      fail: (err) => {
        setIsSendingText(false);
        const errorMessage = get(err, 'data.failure.errors.0.message');
        setSendTextError(errorMessage || 'An unexpected error occurred, please try again');
      },
    }));
  };

  const handleScanData = (data) => {
    setIDScanData(data.IDScanData);
    setIsSecurityModalVisible(true);
  };

  const onReset = () => {
    setIDScanData(null);
  };

  const handleIdScanFail = (err) => {
    idScanSocket && idScanSocket.close(1000, NORMAL_CLOSE);
    idScanSocket = null;
    timeoutTimer && clearTimeout(timeoutTimer);
    setScanErrorMessage(err.message || 'An unexpected error occurred, please try again');
    setIsProgressVisible(false);
    // Enable manual upload fallback for front/back on submission failure
    setIdScanUploadFallback(true);
  };

  const endVerificationProcess = () => {
    idScanSocket && idScanSocket.close(1000, NORMAL_CLOSE);
    idScanSocket = null;
    dispatch(checkinAction({
      key: searchParams.get('key'),
      data: { identityStatus: true },
      success: async (res) => {
        const isProcessing = !get(res, ['data', 'isReady']);
        if (!isProcessing) {
          const iovationBlackBox = await getBlackbox();
          dispatch(nextAction({
            key: searchParams.get('key'),
            data: {
              identityVerified: true,
              iovationBlackBox: iovationBlackBox || '',
            },
            success: (lastNextRes) => {
              if (get(lastNextRes, ['data', 'message', 'text']) || lastNextRes.isReady) {
                if (get(lastNextRes, ['data', 'message', 'text'])) {
                  setScanErrorMessage(get(lastNextRes, ['data', 'message', 'text']));
                  setIsProgressVisible(false);
                }
              }

              if (get(lastNextRes, 'data.url')) {
                navigate(get(lastNextRes, 'data.url'));
              }
            },
            fail: handleIdScanFail,
          }));
        } else {
          handleIdScanFail({
            message: 'An unexpected error occurred, please try again',
          });
        }
      },
      fail: (e) => {
        idScanSocket && idScanSocket.close(1000, NORMAL_CLOSE);
        idScanSocket = null;
        timeoutTimer && clearTimeout(timeoutTimer);
        handleIdScanFail(e);
      },
    }));
  };

  const listenVerificationRes = () => {
    const appId = get(checkinData, 'attributes.entityId');

    idScanSocket = new WebSocket(`${websocketUrl}?application_id=${appId}`);
    console.log('connection:', idScanSocket);
    idScanSocket.addEventListener('message', (event) => {
      console.log('eventData:', event.data);
      const eventData = JSON.parse(get(event, 'data'));
      const eventType = get(eventData, 'EventType');
      if (eventType === 'IDSCAN') {
        dispatch(checkinAction({
          key: searchParams.get('key'),
          data: { identityStatus: true },
          success: async (res) => {
            const isProcessing = !get(res, ['data', 'isReady']);
            if (!isProcessing) {
              idScanSocket && idScanSocket.close(1000, NORMAL_CLOSE);
              idScanSocket = null;
              timeoutTimer && clearTimeout(timeoutTimer);
              const iovationBlackBox = await getBlackbox();

              dispatch(nextAction({
                key: searchParams.get('key'),
                data: {
                  identityVerified: true,
                  iovationBlackBox: iovationBlackBox || ''
                },
                success: (lastNextRes) => {
                  if (get(lastNextRes, ['data', 'message', 'text']) || lastNextRes.isReady) {
                    if (get(lastNextRes, ['data', 'message', 'text'])) {
                      setScanErrorMessage(get(lastNextRes, ['data', 'message', 'text']));
                      setIsProgressVisible(false);
                    }
                  }

                  if (get(lastNextRes, 'data.url')) {
                    navigate(get(lastNextRes, 'data.url'));
                  }
                },
                fail: handleIdScanFail,
              }));
            }
          },
          fail: (e) => {
            idScanSocket && idScanSocket.close(1000, NORMAL_CLOSE);
            idScanSocket = null;
            timeoutTimer && clearTimeout(timeoutTimer);
            handleIdScanFail(e);
          },
        }));
      }
    });

    idScanSocket.addEventListener('close', (event) => {
      if (get(event, 'reason') !== NORMAL_CLOSE || get(event, 'code') !== 1000) {
        console.log('Closed and Reconnecting:', event);
        listenVerificationRes();
      }
    });
  };

  const callNextWorkflow = () => {
    const test = searchParams.get('test');
    const key = searchParams.get('key');

    const formData = {
      ...IDScanData,
      countryCode: 'USA',
      verifyFace: true,
    };

    if (test) {
      formData.test = test;
    }

    window.scrollTo(0, 0);
    setIsProgressVisible(true);
    setSecurityWaitingMessageStep(0);

    convertBase64ToImage(`data:image/png;base64,${formData.frontImageBase64}`, (blob1) => {
      const frontFile = new File([blob1], 'front-image', { type: 'image/png' });
      dispatch(uploadImageToPreSignedUrlAction({
        data: frontFile,
        url: frontImagePreSignedUrl,
        success: () => {
          convertBase64ToImage(`data:image/png;base64,${formData.backOrSecondImageBase64}`, (blob2) => {
            const backFile = new File([blob2], 'back-image', { type: 'image/png' });
            dispatch(uploadImageToPreSignedUrlAction({
              url: backOrSecondImagePreSignedUrl,
              data: backFile,
              success: () => {
                convertBase64ToImage(`data:image/png;base64,${formData.faceImageBase64}`, (blob3) => {
                  const faceFile = new File([blob3], 'face-image', { type: 'image/png' });
                  dispatch(uploadImageToPreSignedUrlAction({
                    data: faceFile,
                    url: selfieImagePreSignedUrl,
                    success: () => {
                      dispatch(nextAction({
                        key,
                        data: {
                          frontImagePreSignedUrl,
                          backOrSecondImagePreSignedUrl,
                          selfieImagePreSignedUrl,
                          countryCode: 'USA',
                          verifyFace: true,
                          documentType: formData.documentType,
                          trackString: formData.trackString || null,
                          captureMethod: formData.captureMethod,
                        },
                        success: (res) => {
                          const isProcessing = !get(res, ['data', 'isReady']);
                          const message = get(res, ['data', 'message', 'text']);
                          if (!isProcessing || message) {
                            if (!isProcessing) {
                              setIsProgressVisible(false);
                            }
                            if (message) {
                              setIsProgressVisible(false);
                              setScanErrorMessage(message);
                            }
                          }
                          if (isProcessing) {
                            timeoutTimer = setTimeout(() => {
                              endVerificationProcess();
                            }, verificationInterval);
                            listenVerificationRes();
                          }
                        },
                        fail: handleIdScanFail,
                      }));
                    },
                    fail: () => handleIdScanFail({ message: 'Unfortunately, we were not able to upload your selfie. Please try again.' }),
                  }));
                });
              },
              fail: () => handleIdScanFail({ message: 'Unfortunately, we were not able to upload the back of your driver’s license. Please try again.' }),
            }));
          });
        },
        fail: () => handleIdScanFail({ message: 'Unfortunately, we were not able to upload the front of your driver’s license. Please try again.' }),
      }));
    });
  };

  const handleAgreeButtonClick = (e) => {
    e.preventDefault();
    setIsSecurityModalVisible(false);
    callNextWorkflow();
  };

  const handleResetScanErrorMessage = (e) => {
    e.preventDefault();
    setScanErrorMessage('');
    onReset();
  };

  if (!hasVerifiedDevice) {
    return (
      <div className="text-center mb-6">
        <Spinner size={12} color="blue-400" />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center text-center gap-8 relative">
      {isProgressVisible && (
        <Spinner
          message={securityWaitingMessage(securityWaitingMessageStep, get(checkinData, 'data.firstName'))}
        />
      )}

      {showIdScan && (
        <IDScan
          handleSubmit={handleScanData}
          onReset={onReset}
          className={`${
            (
              isSecurityModalVisible
              || !!scanErrorMessage
            ) ? 'hidden-uploader-alert-section ' : ''
          }${
            isProgressVisible ? 'hidden h-2' : 'visible'
          }`}
          enableUploadFallback={idScanUploadFallback}
        />
      )}

      {scanErrorMessage && (
        <Modal title="An error occurred" onClose={handleResetScanErrorMessage}>
          <div className="flex flex-col justify-center items-center gap-2">
            <CircleAlert className="h-12 w-12 text-red-500" />
            <p className="text-slate-600 mb-2">{scanErrorMessage}</p>
            <Button className="bg-red-500 text-white w-full" label="OK" onClick={handleResetScanErrorMessage} />
          </div>
        </Modal>
      )}

      {isSecurityModalVisible && (
        <Modal title="Consent for ID Verification" onClose={() => setIsSecurityModalVisible(false)}>
          <>
            <Button className="w-full mb-4" onClick={handleAgreeButtonClick} label="I Agree" />
            <p className="text-base text-slate-600">
              By clicking &quot;I Agree&quot; you give LendingUSA permission via your signature to scan your ID and to share your information with
              {' '}
              <a href="http://idscan.net/">IDScan.net</a>
              , a third-party fraud risk company who uses the information you provided to us to verify your identity and to help prevent ID theft and fraud, and with any State&apos;s Department of Transportation or equivalent agency.
            </p>
          </>
        </Modal>
      )}

      {showDesktopModal && (
        <Modal onClose={() => setShowDesktopModal(false)}>
          <div className="flex flex-col justify-center items-center gap-2">
            <div className="flex items-center mb-2 text-blue-500 w-16 h-16 rounded-full justify-center bg-blue-100 ">
              <TabletSmartphone className="h-8 w-8" />
            </div>
            <h4 className="font-semibold text-lg text-slate-800">This step requires a mobile device</h4>
            <p className="text-sm text-slate-700 mb-3">
              ID verification requires a camera-enabled phone or tablet to complete.
            </p>
            {sendTextError ? (
              <p className="text-sm mb-1 text-red-600 leading-relaxed">
                {sendTextError}
              </p>
            ) : null}
            <FormRow className="w-full px-4">
              <Button
                modifier="outline"
                className="flex-1 text-sm !py-3"
                label="Continue on this device"
                onClick={handleContinueOnDesktop}
              />
              <Button
                className="flex-1 text-sm !py-3"
                label={cooldownSeconds > 0 ? `Resend Text (${cooldownSeconds}s)` : 'Resend Text'}
                onClick={handleResendText}
                isLoading={isSendingText}
                isDisabled={cooldownSeconds > 0}
              />
            </FormRow>
          </div>
        </Modal>
      )}
    </div>
  );
};

VerifyIdentity.propTypes = {
  navigate: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  searchParams: PropTypes.object.isRequired,
};

export default VerifyIdentity;
